import * as React from 'react';
import { addMinutes, differenceInMinutes } from 'owa-datetime';
import type CalendarSurfaceStore from '../../../store/schema/CalendarSurfaceStore';
import convertLayoutPositionToStyles from '../../../utils/convertLayoutPositionToStyles';
import type { CalendarSurfaceTransformProps } from '../../../interfaces/CalendarSurfaceTransformProps';
import type { ItemsLayout } from '../../../interfaces/ItemsLayout';
import type { LayoutInterval } from '../../../interfaces/LayoutInterval';
import { expand } from '../../expand';
import { getDateRange } from '../../getDateRange';
import getDefaultEventDuration from 'owa-session-store/lib/selectors/getDefaultEventDuration';
import { isFeatureEnabled } from 'owa-feature-flags';
import { SurfaceConstants } from 'owa-calendar-surface-core';

let styles = require('../../../SurfaceTemplates.scss');

/**
 * A distance from initial mouse down position we start tracking mouse move
 * Distance is calculated as a sqrt(pow(xi-xc,2) + pow(yi-yc,2))
 * */
const mouseTrackingDistance = 10;

export interface ColumnSelectionHooksProps {
    config: CalendarSurfaceTransformProps;
    surfaceStore: CalendarSurfaceStore;
    columnIndex: number;
    calendarFolderId?: string;
    disableColumnSelection?: boolean;
}

interface DragPoint {
    pageX: number;
    pageY: number;
    currentTarget: HTMLElement;
    clientRect: ClientRect;
}

export default function useColumnSelectionHooks(props: ColumnSelectionHooksProps) {
    let initialPointRef = React.useRef<DragPoint | null>(null);
    let currentPointRef = React.useRef<DragPoint | null>(null);
    const [selectedInterval, setSelectedInterval] = React.useState<LayoutInterval | null>(null);

    const onMouseUp = React.useCallback(
        (ev: MouseEvent) => {
            if (props.config.onSurfaceClick) {
                const dateRange = getDateRange(
                    // recalculating selectedInterval because the state value hasn't updated before this runs, but refs have current values
                    getSelectedInterval(
                        props.config.itemsLayout,
                        props.columnIndex,
                        initialPointRef.current,
                        currentPointRef.current
                    ),
                    props.surfaceStore,
                    props.config.columnProvider
                );

                dateRange.end =
                    isFeatureEnabled('cal-mf-shortenEventBoxSurface') &&
                    differenceInMinutes(dateRange.end, dateRange.start) <
                        SurfaceConstants.DefaultDurationInMinutes
                        ? addMinutes(dateRange.start, SurfaceConstants.DefaultDurationInMinutes)
                        : dateRange.end;

                props.config.onSurfaceClick(
                    dateRange.start,
                    dateRange.end,
                    false,
                    isMouseMoveTrackEnabled(ev, initialPointRef.current),
                    props.calendarFolderId
                );
            }
            initialPointRef.current = null;
            currentPointRef.current = null;
            setSelectedInterval(null);
            document.onmouseup = null;
            document.onmousemove = null;
        },
        [
            props.config.onSurfaceClick,
            props.config.columnProvider,
            props.config.itemsLayout,
            props.columnIndex,
            props.surfaceStore,
            props.calendarFolderId,
        ]
    );

    const onMouseMove = React.useCallback(
        (ev: MouseEvent) => {
            if (!initialPointRef.current || !isMouseMoveTrackEnabled(ev, initialPointRef.current)) {
                return;
            }

            currentPointRef.current = {
                pageX: ev.pageX,
                pageY: ev.pageY,
                currentTarget: null,
                clientRect: initialPointRef.current.currentTarget.getBoundingClientRect(),
            };

            // update selectedinterval
            setSelectedInterval(
                getSelectedInterval(
                    props.config.itemsLayout,
                    props.columnIndex,
                    initialPointRef.current,
                    currentPointRef.current
                )
            );
        },
        [props.config.itemsLayout, props.columnIndex]
    );

    const onMouseDown = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            ev.preventDefault();
            props.config.onSurfaceMouseDown?.(ev);
            if (ev.button == 0) {
                document.onmouseup = onMouseUp;
                document.onmousemove = onMouseMove;

                initialPointRef.current = {
                    pageX: ev.pageX,
                    pageY: ev.pageY,
                    currentTarget: ev.currentTarget,
                    clientRect: ev.currentTarget.getBoundingClientRect(),
                };
            }
        },
        [onMouseMove, onMouseUp]
    );

    const onDoubleClick = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            let { onSurfaceDoubleClick, itemsLayout } = props.config;
            if (onSurfaceDoubleClick) {
                let currentInterval = itemsLayout.pointToInterval(
                    props.columnIndex,
                    ev.pageY,
                    ev.currentTarget.getBoundingClientRect()
                );
                const dateRange = getDateRange(
                    currentInterval,
                    props.surfaceStore,
                    props.config.columnProvider
                );
                onSurfaceDoubleClick(
                    dateRange.start,
                    addMinutes(dateRange.start, getDefaultEventDuration()),
                    false,
                    false,
                    props.calendarFolderId
                );
            }
        },
        [
            props.config.onSurfaceDoubleClick,
            props.config.itemsLayout,
            props.columnIndex,
            props.surfaceStore,
            props.config.columnProvider,
            props.calendarFolderId,
        ]
    );

    const onContextMenu = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => {
            let { onSurfaceRightClick, itemsLayout } = props.config;

            if (onSurfaceRightClick) {
                ev.persist();
                let currentInterval = itemsLayout.pointToInterval(
                    props.columnIndex,
                    ev.pageY,
                    ev.currentTarget.getBoundingClientRect()
                );
                const dateRange = getDateRange(
                    currentInterval,
                    props.surfaceStore,
                    props.config.columnProvider
                );
                onSurfaceRightClick(
                    dateRange.start,
                    addMinutes(dateRange.start, getDefaultEventDuration()),
                    false,
                    ev.nativeEvent,
                    props.calendarFolderId
                );
                ev.stopPropagation();
                ev.preventDefault();
            }
        },
        [
            props.config.onSurfaceRightClick,
            props.config.itemsLayout,
            props.columnIndex,
            props.surfaceStore,
            props.config.columnProvider,
            props.calendarFolderId,
        ]
    );

    const createChildren = React.useCallback(() => {
        if (!selectedInterval) {
            return null;
        }

        const layout = props.config.itemsLayout.getVerticalLayout(selectedInterval);
        let style: React.CSSProperties = convertLayoutPositionToStyles(layout);
        return <div style={style} className={styles.selectionHighlight} />;
    }, [
        selectedInterval,
        props.config.itemsLayout,
        props.config.itemsLayout.getVerticalLayout,
        props.columnIndex,
    ]);

    return props.disableColumnSelection
        ? {
              children: null,
              handlers: {},
          }
        : {
              children: createChildren(),
              handlers: {
                  onMouseDown: onMouseDown,
                  onDoubleClick: onDoubleClick,
                  onContextMenu: onContextMenu,
              },
          };
}

function isMouseMoveTrackEnabled(ev: MouseEvent, initialPoint: DragPoint): boolean {
    if (initialPoint) {
        return (
            Math.sqrt(
                Math.pow(initialPoint.pageX - ev.pageX, 2) +
                    Math.pow(initialPoint.pageY - ev.pageY, 2)
            ) > mouseTrackingDistance
        );
    }
    return false;
}

function getSelectedInterval(
    itemsLayout: ItemsLayout,
    columnIndex: number,
    initialPoint: DragPoint,
    currentPoint: DragPoint
): LayoutInterval {
    let interval = itemsLayout.pointToInterval(
        columnIndex,
        initialPoint.pageY,
        initialPoint.clientRect
    );

    if (currentPoint) {
        let currentInterval = itemsLayout.pointToInterval(
            columnIndex,
            currentPoint.pageY,
            currentPoint.clientRect
        );

        expand(interval, currentInterval);
    }

    return interval;
}
